// src/pages/student/StudentProfileForm.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { storage } from '../../firebase/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// FIX: Import TermsModal from its new location
import TermsModal from '../../components/TermsModal';

// --- Subject List Constants ---
const grade8to10Subjects = ['Math', 'English', 'Science', 'Social Studies'];
const scienceSubjects = ['Physics', 'Chemistry', 'Biology', 'Math', 'English Communicative', 'English Core'];
const commerceSubjects = ['Math', 'Economics', 'Business Studies', 'English Communicative', 'English Core', 'Accountancy'];
const humanitiesSubjects = ['Economics', 'Political Science', 'History', 'Geography', 'English Communicative', 'English Core'];

// --- Checkbox Options Lists ---
const competitiveOptions = [
    { label: 'UPSC Prelims', value: 'upsc_prelims' },
    { label: 'UPSC Mains', value: 'upsc_mains' },
    { label: 'SSC CGL', value: 'ssc_cgl' },
    { label: 'SSC CHSL', value: 'ssc_chsl' },
    { label: 'SSC Steno C & D', value: 'ssc_steno_c_d' },
];
const entranceOptions = [
    { label: 'CUET', value: 'CUET' },
    { label: 'CTET (Paper 1)', value: 'ctet_paper1' },
    { label: 'CTET (Paper 2 Maths/Science)', value: 'ctet_paper2_math_science' },
    { label: 'CTET (Paper 2 Social)', value: 'ctet_paper2_social' },
    { label: 'JEE', value: 'JEE' },
    { label: 'NEET', value: 'NEET' },
];


// --- Dynamic Subject Checkbox Component ---
const SubjectCheckboxes = ({ subjects, selected, onChange }) => (
    <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Subjects (Select all that apply) *</label>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {subjects.map(subject => (
                <label key={subject} className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-100 cursor-pointer">
                    <input type="checkbox" checked={selected.includes(subject)} onChange={() => onChange(subject)} className="rounded text-indigo-600"/>
                    <span>{subject}</span>
                </label>
            ))}
        </div>
    </div>
);

// --- Generic Checkbox List Component ---
const CheckboxList = ({ options, selected, onChange }) => (
       <div className="space-y-2">
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                {options.map(option => (
                    <label key={option.value} className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-100 cursor-pointer">
                        <input type="checkbox" checked={selected.includes(option.value)} onChange={() => onChange(option.value, selected)} className="rounded text-indigo-600"/>
                        <span>{option.label}</span>
                    </label>
                ))}
            </div>
        </div>
);


// ===================================================================
// COMPONENT: StudentProfileForm
// ===================================================================
export default function StudentProfileForm() {
    const { user, userProfile, updateStudentProfile } = useAuth();

    // Form state
    const [name, setName] = useState(userProfile?.displayName || user?.displayName || '');
    const [dob, setDob] = useState(userProfile?.dob || '');
    const [phone, setPhone] = useState(userProfile?.phone || user?.phoneNumber || '');

    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(userProfile?.photoURL || user?.photoURL || null);

    const [signatureFile, setSignatureFile] = useState(null);
    const [signaturePreview, setSignaturePreview] = useState(userProfile?.signatureURL || null);

    // Exam interest state
    const [examCategory, setExamCategory] = useState(userProfile?.examInterests?.category || '');
    const [schoolGrade, setSchoolGrade] = useState(userProfile?.examInterests?.school?.grade || '');
    const [schoolBoard, setSchoolBoard] = useState(userProfile?.examInterests?.school?.board || '');
    const [schoolState, setSchoolState] = useState(userProfile?.examInterests?.school?.state || '');
    const [schoolSubjects, setSchoolSubjects] = useState(userProfile?.examInterests?.school?.subjects || []);
    const [schoolStream, setSchoolStream] = useState(userProfile?.examInterests?.school?.stream || '');
    const [wantsLearning, setWantsLearning] = useState(userProfile?.wantsLearning || false);

    const [competitiveExams, setCompetitiveExams] = useState(userProfile?.examInterests?.competitive || []);
    const [entranceExams, setEntranceExams] = useState(userProfile?.examInterests?.entrance || []);

    // Parent Consent state
    const [parentName, setParentName] = useState(userProfile?.parentConsent?.name || '');
    const [parentRelationship, setParentRelationship] = useState(userProfile?.parentConsent?.relationship || '');

    // T&C state
    const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
    const [hasAgreedToTerms, setHasAgreedToTerms] = useState(false);

    // UI State
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Age Calculation
    const calculatedAge = useMemo(() => {
        if (!dob) return null;
        try {
            const birthDate = new Date(dob);
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const m = today.getMonth() - birthDate.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) { age--; }
            return age;
        } catch (e) { return null; }
    }, [dob]);

    // Checkbox handler for multi-select lists
    const handleCheckboxChange = useCallback((value, stateArray, setState) => {
        setState(prev =>
            prev.includes(value)
                ? prev.filter(item => item !== value)
                : [...prev, value]
        );
    }, []);

    // Specific handler for school subjects
    const handleSchoolSubjectChange = useCallback((subject) => {
        setSchoolSubjects(prev =>
            prev.includes(subject)
                ? prev.filter(s => s !== subject)
                : [...prev, subject]
        );
    }, []);

    // File upload handler
    const handleFileChange = (e, setFile, setPreview, maxMB = 2, type = 'image') => {
        const file = e.target.files[0];
        if (!file) return;
        const typeMatch = type === 'image' ? file.type.startsWith('image/') : file.type === 'application/pdf' || file.type.startsWith('image/');
        if (!typeMatch) { setError(`Invalid file type. Please upload a valid ${type}.`); setFile(null); setPreview(null); e.target.value = null; return; }
        if (file.size > maxMB * 1024 * 1024) { setError(`File is too large (Max ${maxMB}MB).`); setFile(null); setPreview(null); e.target.value = null; }
        else { setFile(file); setPreview(URL.createObjectURL(file)); setError(''); }
    };

    // Form Submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true); setError(''); setSuccess('');

        // --- Validation ---
        if (!hasAgreedToTerms) { setError("You must read and accept the Terms & Conditions."); setLoading(false); return; }
        if (calculatedAge === null) { setError("Invalid Date of Birth."); setLoading(false); return; }
        if (examCategory === 'school' && (!parentName.trim() || !parentRelationship.trim())) { setError("Parent/Guardian consent is required for school exam applicants."); setLoading(false); return; }
        if (examCategory === 'competitive' && calculatedAge < 18) { setError(`You must be 18+ for competitive exams. Your age: ${calculatedAge}.`); setLoading(false); return; }
        if (competitiveExams.includes('upsc_prelims') && calculatedAge < 20) { setError(`You must be 20+ for UPSC. Your age: ${calculatedAge}.`); setLoading(false); return; }
        if (examCategory === 'entrance' && calculatedAge < 16) { setError(`You must be 16+ for entrance exams. Your age: ${calculatedAge}.`); setLoading(false); return; }
        if (examCategory === 'school' && (calculatedAge < 13 || calculatedAge > 19)) { setError(`School exams are typically for ages 13-19. Your age is ${calculatedAge}.`); setLoading(false); return; }
        if (!photoFile && !userProfile?.photoURL) { setError("Please upload a profile photograph."); setLoading(false); return; }
        if (!signatureFile && !userProfile?.signatureURL) { setError("Please upload a scan of your signature."); setLoading(false); return; }
        // --- End Validation ---

        try {
            let finalPhotoUrl = photoPreview;
            let finalSignatureUrl = signaturePreview;

            if (photoFile) {
                const photoRef = ref(storage, `student_photos/${user.uid}/${photoFile.name}`);
                const photoSnap = await uploadBytes(photoRef, photoFile);
                finalPhotoUrl = await getDownloadURL(photoSnap.ref);
            }
            if (signatureFile) {
                const sigRef = ref(storage, `student_signatures/${user.uid}/${signatureFile.name}`);
                const sigSnap = await uploadBytes(sigRef, signatureFile);
                finalSignatureUrl = await getDownloadURL(sigSnap.ref);
            }

            // --- Collect all exam interests ---
            let schoolData = null;
            if (examCategory === 'school') {
                if (['VIII', 'IX', 'X'].includes(schoolGrade)) {
                    schoolData = { grade: schoolGrade, board: schoolBoard, state: schoolBoard === 'state' ? schoolState : null, subjects: schoolSubjects };
                } else if (['XI', 'XII'].includes(schoolGrade)) {
                    schoolData = { grade: schoolGrade, board: schoolBoard, state: schoolBoard === 'state' ? schoolState : null, stream: schoolStream, subjects: schoolSubjects };
                }
            }
            const examInterests = {
                category: examCategory,
                school: schoolData,
                competitive: competitiveExams,
                entrance: entranceExams,
            };
            // --- End collecting interests ---

            const parentConsent = (examCategory === 'school')? { name: parentName.trim(), relationship: parentRelationship.trim() } : null;

            const profileData = {
                displayName: name, dob: dob, phone: phone,
                photoURL: finalPhotoUrl,
                signatureURL: finalSignatureUrl,
                examInterests: examInterests,
                parentConsent: parentConsent,
                termsAccepted: new Date(),
                wantsLearning: (examCategory === 'school') ? wantsLearning : false,
            };

            const result = await updateStudentProfile(user.uid, profileData);
            setSuccess(result.message || "Profile updated successfully!");

        } catch (err) {
            console.error("Error updating profile:", err);
            setError(`Failed to update: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-xl border border-gray-200 max-w-3xl mx-auto">
            {isTermsModalOpen && ( <TermsModal onAccept={() => { setHasAgreedToTerms(true); setIsTermsModalOpen(false); }} onClose={() => setIsTermsModalOpen(false)} /> )}

            <h3 className="text-2xl font-bold text-gray-800 mb-2">Welcome, {name || 'Student'}!</h3>
            <p className="text-gray-600 mb-6">Please complete your profile to continue. This information is required to provide relevant services.</p>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M8.257 3.099c.636-1.176 2.85-1.176 3.486 0l5.58 10.306c.636 1.176-.477 2.62-1.743 2.62H4.42c-1.266 0-2.379-1.444-1.743-2.62l5.58-10.306zM9 13a1 1 0 112 0 1 1 0 01-2 0zm1-7a1 1 0 00-1 1v4a1 1 0 102 0V7a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                    </div>
                    <div className="ml-3">
                        <p className="text-sm text-yellow-700">Please review all details carefully before submission. Providing fake details may result in your account being banned. Profile information (like Name, DOB, and documents) **cannot be edited** after submission.</p>
                    </div>
                </div>
            </div>

            {error && <div className="text-red-700 bg-red-100 p-3 rounded-md mb-4 border border-red-300">{error}</div>}
            {success && <div className="text-green-700 bg-green-100 p-3 rounded-md mb-4 border border-green-300">{success}</div>}

            <form onSubmit={handleSubmit} className="space-y-6">

                <fieldset className="space-y-4">
                    <legend className="text-lg font-semibold text-gray-800 border-b pb-2 mb-3">Personal Details</legend>
                    <div><label className="block text-sm font-medium text-gray-700">Full Name *</label><input type="text" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"/></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><label className="block text-sm font-medium text-gray-700">Date of Birth *</label><input type="date" value={dob} onChange={(e) => setDob(e.target.value)} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"/></div>
                        <div><label className="block text-sm font-medium text-gray-700">Age (Auto-calculated)</label><input type="text" value={calculatedAge !== null ? calculatedAge : "N/A"} disabled className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm bg-gray-100"/></div>
                    </div>
                    <div><label className="block text-sm font-medium text-gray-700">Phone Number *</label><input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="e.g., 9876543210" required className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"/></div>
                </fieldset>

                   <fieldset className="space-y-4">
                       <legend className="text-lg font-semibold text-gray-800 border-b pb-2 mb-3">Uploads</legend>
                       <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                           <div className="flex items-center gap-4">
                               <img src={photoPreview || `https://api.dicebear.com/7.x/initials/svg?seed=${name || 'User'}`} alt="Profile" className="w-20 h-20 rounded-full object-cover bg-gray-200 border border-gray-300"/>
                               <label className="block">
                                   <span className="block text-sm font-medium text-gray-700">Profile Photograph * <span className="text-xs text-gray-500">(Max 2MB)</span></span>
                                   <input type="file" onChange={(e) => handleFileChange(e, setPhotoFile, setPhotoPreview, 2, 'image')} accept="image/png, image/jpeg" className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer"/>
                                   {photoFile && <span className="text-xs text-green-600 mt-1">{photoFile.name} selected</span>}
                               </label>
                           </div>
                           <div className="flex items-center gap-4">
                               <div className="w-20 h-20 flex items-center justify-center bg-gray-200 border border-gray-300 rounded-md p-1">
                                   {signaturePreview ? <img src={signaturePreview} alt="Signature" className="h-full w-full object-contain"/> : <span className="text-xs text-gray-500">Preview</span>}
                               </div>
                               <label className="block">
                                   <span className="block text-sm font-medium text-gray-700">Scanned Signature * <span className="text-xs text-gray-500">(Max 1MB, image)</span></span>
                                   <input type="file" onChange={(e) => handleFileChange(e, setSignatureFile, setSignaturePreview, 1, 'image')} accept="image/png, image/jpeg" className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer"/>
                                   {signatureFile && <span className="text-xs text-green-600 mt-1">{signatureFile.name} selected</span>}
                               </label>
                           </div>
                       </div>
                   </fieldset>

                <fieldset className="space-y-4">
                    <legend className="text-lg font-semibold text-gray-800 border-b pb-2 mb-3">Exam Interests *</legend>
                    <select value={examCategory} onChange={(e) => {setExamCategory(e.target.value); setSchoolGrade(''); setSchoolBoard(''); setSchoolStream(''); setSchoolSubjects([]); setCompetitiveExams([]); setEntranceExams([]);}} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm bg-white">
                        <option value="">-- Select a Category --</option>
                        <option value="school">School Exams (Age 14-19)</option>
                        <option value="competitive">Competitive Exams (Age 18+)</option>
                        <option value="entrance">Entrance Exams (Age 16+)</option>
                    </select>

                    {/* --- School Options --- */}
                    {examCategory === 'school' && (
                        <div className="space-y-4 p-4 border rounded-md bg-gray-50">
                            <select value={schoolGrade} onChange={(e) => { setSchoolGrade(e.target.value); setSchoolSubjects([]); setSchoolStream(''); }} required={examCategory === 'school'} className="p-2 border border-gray-300 rounded-md bg-white w-full"><option value="">-- Select Grade --</option><option value="VIII">Grade VIII</option><option value="IX">Grade IX</option><option value="X">Grade X</option><option value="XI">Grade XI</option><option value="XII">Grade XII</option></select>

                            {schoolGrade && (
                                <div className="space-y-4">
                                    <select value={schoolBoard} onChange={(e) => setSchoolBoard(e.target.value)} required={!!schoolGrade} className="p-2 border border-gray-300 rounded-md bg-white w-full"><option value="">-- Select Board --</option><option value="CBSE">CBSE</option><option value="ICSE">ICSE</option><option value="state">State Board</option></select>
                                    {schoolBoard === 'state' && (<input type="text" value={schoolState} onChange={(e) => setSchoolState(e.target.value)} placeholder="Enter State Name" required={schoolBoard === 'state'} className="p-2 border border-gray-300 rounded-md w-full"/>)}
                                </div>
                            )}

                            {['VIII', 'IX', 'X'].includes(schoolGrade) && (
                                <SubjectCheckboxes subjects={grade8to10Subjects} selected={schoolSubjects} onChange={handleSchoolSubjectChange} />
                            )}
                            {['XI', 'XII'].includes(schoolGrade) && (
                                <div className="space-y-4">
                                    <select value={schoolStream} onChange={(e) => { setSchoolStream(e.target.value); setSchoolSubjects([]); }} required={['XI', 'XII'].includes(schoolGrade)} className="p-2 border border-gray-300 rounded-md bg-white w-full"><option value="">-- Select Stream --</option><option value="Science">Science</option><option value="Commerce">Commerce</option><option value="Humanities">Humanities (Arts)</option></select>
                                    {schoolStream === 'Science' && <SubjectCheckboxes subjects={scienceSubjects} selected={schoolSubjects} onChange={handleSchoolSubjectChange} />}
                                    {schoolStream === 'Commerce' && <SubjectCheckboxes subjects={commerceSubjects} selected={schoolSubjects} onChange={handleSchoolSubjectChange} />}
                                    {schoolStream === 'Humanities' && <SubjectCheckboxes subjects={humanitiesSubjects} selected={schoolSubjects} onChange={handleSchoolSubjectChange} />}
                                </div>
                            )}

                            {/* --- Live Class Checkbox --- */}
                            {schoolGrade && (
                                <div className="mt-4 pt-4 border-t">
                                     <label className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-gray-100 rounded-md">
                                       <input
                                            type="checkbox"
                                            checked={wantsLearning}
                                            onChange={(e) => setWantsLearning(e.target.checked)}
                                            className="rounded text-indigo-600"
                                        />
                                        <span className="font-medium text-gray-700">I am also interested in Online Courses / Live Classes for my selected grade.</span>
                                    </label>
                                </div>
                            )}
                        </div>
                    )}
                    {/* --- Competitive Options (Checkboxes) --- */}
                    {examCategory === 'competitive' && (
                        <div className="p-4 border rounded-md bg-gray-50">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Select Competitive Exams (Select all that apply) *</label>
                            <CheckboxList options={competitiveOptions} selected={competitiveExams} onChange={(val) => handleCheckboxChange(val, competitiveExams, setCompetitiveExams)} />
                        </div>
                    )}
                    {/* --- Entrance Options (Checkboxes) --- */}
                    {examCategory === 'entrance' && (
                        <div className="p-4 border rounded-md bg-gray-50">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Select Entrance Exams (Select all that apply) *</label>
                            <CheckboxList options={entranceOptions} selected={entranceExams} onChange={(val) => handleCheckboxChange(val, entranceExams, setEntranceExams)} />
                        </div>
                    )}
                </fieldset>

                {/* --- Parent Consent (Conditional) --- */}
                {examCategory === 'school' && (
                    <fieldset className="space-y-4 p-4 border rounded-md bg-blue-50 border-blue-200">
                        <legend className="text-lg font-semibold text-gray-800 border-b pb-2 mb-3">Parent/Guardian Consent *</legend>
                        <p className="text-sm text-gray-600">Consent is required for students applying for school-level exams.</p>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Parent/Guardian Full Name *</label>
                            <input type="text" value={parentName} onChange={(e) => setParentName(e.target.value)} required={examCategory === 'school'} className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Relationship to Student *</label>
                            <input type="text" value={parentRelationship} onChange={(e) => setParentRelationship(e.target.value)} placeholder="e.g., Father, Mother, Guardian" required={examCategory === 'school'} className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"/>
                        </div>
                    </fieldset>
                )}

                {/* --- Terms & Conditions --- */}
                <fieldset>
                    <div className="flex items-start">
                        <input id="terms" name="terms" type="checkbox" checked={hasAgreedToTerms} onChange={(e) => setHasAgreedToTerms(e.target.checked)} className="h-4 w-4 text-indigo-600 border-gray-300 rounded mt-1 cursor-pointer"/>
                        <div className="ml-3 text-sm">
                            <label htmlFor="terms" className="font-medium text-gray-700">
                                I have read, understood, and agree to the
                                <button type="button" onClick={() => setIsTermsModalOpen(true)} className="ml-1 text-indigo-600 hover:text-indigo-800 font-semibold underline">
                                    Terms & Conditions and Privacy Policy
                                </button>
                                  . *
                            </label>
                        </div>
                    </div>
                </fieldset>

                <div className="text-right pt-4">
                    <button type="submit" disabled={loading || !hasAgreedToTerms || success} className="bg-green-600 text-white py-3 px-8 rounded-md font-semibold text-lg hover:bg-green-700 transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed">
                        {loading ? "Saving..." : (success ? "Profile Saved!" : "Save Profile & Continue")}
                    </button>
                </div>
            </form>
        </div>
    );
}
